import type { UniverseId } from '@/domain/universes/universe.interface';
import type { AccessLevel } from '@/interfaces/Access.interface';
import type { BuildingId } from '@/interfaces/Building.interface';
import type { UnzonedDateTime } from '@/interfaces/Date.interface';
import type { SensorTimeseriesId } from '@/interfaces/Timeseries.interface';
import type { UnitCode } from '@/interfaces/Unit.interface';
import type { LiteralValues } from '@/utils/typing/literal-values.type';
import type {
    AlertOccupancyMode,
    AlertScenario,
    AlertScenarioId,
    AlertScenarioStatus,
    AlertScenarioType,
    NullableTimeseriesPoint,
    ScenarioActivity,
    ScenarioBuildingOccupancyTimeseries,
    TimeseriesPoint
} from '../alerts.interface';

export const DriftTargetMeasure = {
    NIGHT_SETBACK: 'night_setback',
    BASE_PEAK_LOAD_RATIO: 'base_peak_load_ratio'
} as const;
export type DriftTargetMeasure = LiteralValues<typeof DriftTargetMeasure>;

export const DriftSensitivityLevel = {
    LOW: 'low',
    MEDIUM: 'medium',
    HIGH: 'high'
} as const;
export type DriftSensitivityLevel = LiteralValues<typeof DriftSensitivityLevel>;

export interface DriftScenarioBuildingLevelAbruptStat {
    buildingId: BuildingId;
    abruptAlertCount: number;
}

export interface DriftScenarioBuildingLevelGradualStat {
    buildingId: BuildingId;
    alertedValPercent: number;
    alertedVal: number;
    formattedAlertedVal: string;
    activeScenarioVal: number;
}

export interface DriftScenarioSenLevelAbruptStat {
    buildingId: BuildingId;
    sensorCount: number;
    abruptAlertCount: number;
}

export interface DriftScenarioSenLevelGradualStat {
    buildingId: BuildingId;
    sensorCount: number;
    alertedValPercent: number;
    alertedVal: number;
    activeScenarioVal: number;
    formattedAlertedVal: string;
}

export const DriftMeasureLevel = {
    BUILDING: 'BUILDING',
    SEN: 'SEN'
} as const;

export type DriftMeasureLevel = LiteralValues<typeof DriftMeasureLevel>;

export const DriftAbruptAlertTypeCode = {
    ABRUPT_DRIFT_UP: 'abrupt_drift_up',
    ABRUPT_DRIFT_DOWN: 'abrupt_drift_down'
} as const;

export type DriftAbruptAlertTypeCode = LiteralValues<typeof DriftAbruptAlertTypeCode>;

export const DriftGradualAlertTypeCode = {
    GRADUAL_DRIFT_UP: 'gradual_drift_up',
    GRADUAL_DRIFT_DOWN: 'gradual_drift_down'
} as const;

export type DriftGradualAlertTypeCode = LiteralValues<typeof DriftGradualAlertTypeCode>;

export const DriftAlertType = {
    GRADUAL: 'gradual',
    ABRUPT: 'abrupt'
} as const;

export type DriftAlertType = LiteralValues<typeof DriftAlertType>;

export interface DriftAlertScenarioCreation {
    name: string;
    universeId: UniverseId;
    targetMeasure: DriftTargetMeasure;
    measureLevel: DriftMeasureLevel;
}

export interface DriftScenario {
    scenario: AlertScenario;
    driftConfig: DriftScenarioConfig;
}

export interface DriftScenarioConfig {
    universeId: UniverseId;
    targetMeasure: DriftTargetMeasure;
    measureLevel: DriftMeasureLevel;
    sensitivityLevel: DriftSensitivityLevel;
    activity: ScenarioActivity;
}

export interface DriftScenarioFilters {
    search: string | null;
    status: AlertScenarioStatus | null;
    targetMeasure: DriftTargetMeasure | null;
    measureLevel: DriftMeasureLevel | null;
}

export interface DriftScenarioUpdate {
    name?: string;
    driftConfig?: Partial<DriftScenarioConfig>;
}

export interface DriftAlertsAnalysis {
    targetMeasureTimeseries: NullableTimeseriesPoint[];
    targetMeasureTimeseriesUnitCode: UnitCode;
    targetMeasureSourceTimeseries: TimeseriesPoint[];
    targetMeasureSourceTimeseriesUnitCode: UnitCode;
    scenarioActivity: ScenarioBuildingOccupancyTimeseries[];
    abruptAlerts: AbruptDriftAlert[];
    gradualAlerts: GradualDriftAlert[];
}

export interface AbruptDriftAlert {
    alertTypeCode: DriftAbruptAlertTypeCode;
    status: string;
    occurredAt: UnzonedDateTime;
}

export interface GradualDriftAlert {
    alertTypeCode: DriftGradualAlertTypeCode;
    status: string;
    startedAt: UnzonedDateTime;
    endedAt: UnzonedDateTime;
}

export interface DriftScenarioSensorStat {
    timeseriesId: SensorTimeseriesId;
}

export interface DriftGlobalStats {
    abruptAlertCount: number;

    impactedBuildingCount: number;
    buildingCount: number;
    buildingConsumption: number;
    impactedBuildingConsumption: number;

    impactedSensorCount: number;
    sensorCount: number;
    sensorConsumption: number;
    impactedSensorConsumption: number;
}

export interface DriftScenarioSummary {
    alertScenarioId: AlertScenarioId;
    access: AccessLevel;
    scenarioType: AlertScenarioType;
    name: string;
    status: AlertScenarioStatus;
    universeId: UniverseId;
    targetMeasure: DriftTargetMeasure;
    measureLevel: DriftMeasureLevel;
    occupancy: AlertOccupancyMode;
    abruptAlertCount: number;
    impactedBuildingCount: number | null;
    buildingCount: number | null;
    consumptionImpactedBuildingPercent: number | null;
    impactedSensorCount: number | null;
    sensorCount: number | null;
    consumptionImpactedSensorPercent: number | null;
    abruptAlertProgress: number | null;
    impactedBuildingProgress: number | null;
    impactedSensorProgress: number | null;
    alertedVal: string;
    alertedValPercent: number;
    alertedValPercentProgress: number | null;
}
